import { useLayoutEffect, useRef, type JSX } from 'react'
import { sanitizeHtml } from './sanitizeHtml'

/**
 * Shared markup, rendered through the sanitiser.
 *
 * This replaces dangerouslySetInnerHTML everywhere in the viewer. The
 * sanitiser hands back checked DOM nodes rather than a string, and they are
 * inserted as they are -- never serialised and parsed a second time, which is
 * where markup that was harmless in the first parse can turn executable in the
 * next. React owns the wrapper and never renders children into it, so it
 * leaves these nodes alone.
 *
 * The wrapper carries `safe-html` (see index.css): paint containment, so
 * nothing inside -- however it is positioned -- can draw outside this box or
 * over the viewer's own controls.
 */
export function SafeHtml({ html, className }: { html: string; className?: string }): JSX.Element {
  const ref = useRef<HTMLDivElement | null>(null)
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    el.replaceChildren(sanitizeHtml(html, el.ownerDocument))
  }, [html])
  return <div ref={ref} className={className ? `safe-html ${className}` : 'safe-html'} />
}
